import React, { useEffect, useState, useRef } from 'react';
import { User, Users, FileCheck, Loader, CheckCircle2, ShieldCheck, ArrowDownToDot, ClipboardCheck } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { FaDownload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './adminDashboard.css';
import { getDashboardData, getRecentNotifications, getRecentCandidates, getVerifiedCandidates } from '../../../services/admin/api';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalClients: 0,
    activeEmployees: 0,
    inactiveEmployees: 0,
    readyForBGCandidates: 0,
    workInProgressCandidates: 0,
    completedCandidates: 0,
    verifiedCandidates: 0,
  });
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recentCandidates, setRecentCandidates] = useState([]);
  const cardsRef = useRef([]);
  const [verifiedCandidates, setverifiedCandidates] = useState([]);
  const navigate = useNavigate();

  const username = localStorage.getItem('user_name') || '';

  const activityRef = useRef(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await getDashboardData();
        console.log("Dashboard Response:", response.data);
        if (response.data && response.data.status && response.data.data) {
          setDashboardData(response.data.data);
        } else {
          setError('Failed to fetch dashboard data.');
        }
      } catch (err) {
        setError('Something went wrong while fetching dashboard data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

const fetchActivityFeed = async () => {
  try {
    const response = await getRecentNotifications();


    if (response.status && Array.isArray(response.data)) {
      const sorted = [...response.data].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setActivityFeed(sorted);
    } else {
      setActivityFeed([]);
    }
  } catch (err) {
    setActivityFeed([]);
  }
};


    const recentCandidateList = async () => {
      try {
        const response = await getRecentCandidates();

        if (response?.data?.data && Array.isArray(response.data.data)) {
          setRecentCandidates(response.data.data);
        } else {
          setRecentCandidates([]);
        }
      } catch (err) {
        setRecentCandidates([]);
      }
    };

    const verifiedCandidateList = async () => {
      try {
        const response = await getVerifiedCandidates();

        if (response?.data?.data && Array.isArray(response.data.data)) {
          setverifiedCandidates(response.data.data);
        } else {
          setverifiedCandidates([]);
        }
      } catch (err) {
        setverifiedCandidates([]);
      }
    };

    fetchDashboardData();
    fetchActivityFeed();
    recentCandidateList();
    verifiedCandidateList();
  }, []);


  const formatTimeAgo = (date) => {
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(date).toLocaleDateString();
  };

  const normalizeStatus = (status) => {
    if (!status) return "";

    return status
      .toLowerCase()
      .replace(/_received$/, "")  
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };



  if (loading)
    return <div className="centered-message">
      <div className="spinner"></div>
      <div>Loading Dashboard ...</div>
    </div>;

  if (error)
    return <div style={{ color: 'red', textAlign: 'center', padding: '50px', fontSize: '1.2rem' }}>{error}</div>;

  const stats = [
    {
      title: 'Total Approved Clients',
      count: dashboardData.totalClients,
      icon: <User size={32} color="#4f46e5" />,
      path: '/admin/clients'
    },

    {
      title: 'Ready for BGC',
      count: dashboardData.readyForBgcCandidate,
      icon: <ArrowDownToDot size={32} color="#e41313" />,
      path: '/admin/candidates/READY'
    },

    {
      title: 'Work In Progress',
      count: dashboardData.workInProgressCandidates,
      icon: <Loader size={32} color="#eab308" />,
      path: '/admin/candidates/WIP'
    },

    {
      title: 'Verified',
      count: dashboardData.verifiedCandidates,
      icon: <ShieldCheck size={32} color="#0ea5e9" />,
      path: '/admin/candidates/VERIFIED'
    },

    {
      title: 'Completed',
      count: dashboardData.completedCandidates,
      icon: <CheckCircle2 size={32} color="#10b981" />,
      path: '/admin/candidates/COMPLETED'
    },


  ];


  const pieChartData = [
    { name: 'RFB', value: dashboardData.readyForBgcCandidate },
    { name: 'WIP', value: dashboardData.workInProgressCandidates },
    { name: 'Completed', value: dashboardData.completedCandidates },
    { name: 'Verified', value: dashboardData.verifiedCandidates },
  ];


  const employeeStatusData = [
    { name: 'Active Employees', value: dashboardData.activeEmployees },
    { name: 'Inactive Employees', value: dashboardData.inactiveEmployees }
  ];



  const COLORS = ['#16a34a', '#f97316'];
  const PIE_COLORS = ['#4f46e5', '#16a34a', '#f97316', '#eab308', '#a4de6c'];

  return (
    <div className="dashboard-container">
      <div className="welcome-section">
        <h1 className="welcome-title">Welcome back, {username}</h1>
        <button
          className="add-candidate-button"
          onClick={() => navigate('/admin/add-employee')}
        >
          + Add Agent
        </button>
      </div>

      <div className="dashboard-grid">
        {stats.map((stat, index) => (
          <div
            className={`admin-card clickable ${stat.path ? 'clickable-card' : ''}`}
            key={index}
            ref={el => cardsRef.current[index] = el}
            onClick={() => stat.path && navigate(stat.path)}
          >
            <p className="admin-card-title">{stat.title}</p>
            <div className="admin-icon-cout-cont">
              <p className="admin-card-count">{stat.count}</p>
              <div className="admin-icon-wrapper" >
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="main-dashboard-content">
        <div className="card overall-status-card">
          <h2 className="card-header">Overall candidates status</h2>
          <div className="overall-status-content">
            <div className="pie-chart-wrapper">
              <ResponsiveContainer width="110%" height="110%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    labelLine={false}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, props) => [`${value}`, props.payload.name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="pie-chart-legend vertical-legend">
              {pieChartData.map((entry, index) => (
                <div key={`legend-${index}`} className="legend-item">
                  <span className="legend-color" style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}></span>
                  {entry.name}: {entry.value}
                </div>
              ))}
            </div>
          </div>
        </div>

       <div className="card activity-feed-card">
  <h2 className="card-header">Activity feed</h2>

  <div
    className="scrollable-card-list"
    ref={activityRef}
    style={{
      maxHeight: '300px',
      overflowY: 'auto',
      scrollBehavior: 'smooth'
    }}
  >
    {activityFeed.length > 0 ? (
      activityFeed.map((activity, index) => (
        <div key={`activity-${index}`} className="list-card">
          <div className="activity-row">
            <span className="activity-text">
              {activity.message}
            </span>

            <span className="activity-time">
              {formatTimeAgo(activity.createdAt)}
            </span>
          </div>
        </div>
      ))
    ) : (
      <div className="list-card">No recent notifications</div>
    )}
  </div>
</div>


        <div className="bottom-sections-container">
          <div className="card overall-status-card">
            <h2 className="card-header">Recent Candidates</h2>

            <div className="card-scroll-body">
              {recentCandidates.length > 0 ? (
                recentCandidates.map((item, index) => (
                  <div className="list-card" key={`recent-${index}`}>
                    <div className="list-card-left">
                      <div>
                        <div className="client-name">{item.fullName}</div>
                        <div className="status-text">
                          {item.caseInitiatedDateOnly
                            ? new Date(item.caseInitiatedDateOnly).toLocaleDateString()
                            : 'N/A'}
                        </div>
                      </div>
                    </div>
                    <span className="report-status">
                      {normalizeStatus(item.bgcStatus)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="list-card">No Recent Candidates available</div>
              )}
            </div>
          </div>

          <div className="card overall-status-card">
            <h2 className="card-header">Verified Candidates</h2>
            <div className="card-scroll-body">
              {verifiedCandidates.length > 0 ? (
                verifiedCandidates.map((item, index) => (
                  <div className="list-card" key={`rprt-${index}`}>
                    <div className="list-card-left">
                      <div>
                        <div className="client-name">{item.fullName}</div>
                        <div className="status-text">Candidate: {item.candidateId}</div>
                        <div className="status-text">
                          {item.caseInitiatedDateOnly ? new Date(item.caseInitiatedDateOnly).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                    </div>
                    <span className="report-status">{normalizeStatus(item.bgcStatus)}</span>
                  </div>
                ))
              ) : (
                <div className="list-card">No Recent Candidates available</div>
              )}
            </div>
          </div>

          <div className="card overall-status-card">
            <h2 className="card-header">Employee Status Overview</h2>

            <div className="overall-status-content">
              <div className="pie-chart-wrapper">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={employeeStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {employeeStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="pie-chart-legend vertical-legend">
                {employeeStatusData.map((entry, index) => (
                  <div key={index} className="legend-item">
                    <span className="legend-color" style={{ backgroundColor: COLORS[index] }}></span>
                    {entry.name}: {entry.value}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
